import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import Axios from 'axios';
import { AccountDataType, BillingContactForm, SignUpForm } from 'types/globalTypes';
import { errorCatch } from './errorCatcher.reducer';
import { Client } from './hiringClient.reducer';

export type SubscriptionDataType = {
  employeesNumber: number | null;
  hiringClient: Client | null;
};

export type SpecialPricingData = {
  sku: string | null;
  specialPricingId: string | null;
};

export type InitState = {
  signUpData: SignUpForm;
  billingData: BillingContactForm;
  subscriptionData: SubscriptionDataType;
  specialPricingData: SpecialPricingData;
  isSubcontractor: boolean;
};

export const initialState = {
  signUpData: {
    // Administrator Section fields
    firstName: '',
    lastName: '',
    title: '',
    email: '',
    phone: '',

    // Company Address  fields
    country: '',
    countryIso3: '',
    region: '',
    city: '',
    address1: '',
    address2: '',
    companyEmail: '',
    companyPhoneNumber: '',
    postalCode: '',
    companyWebsite: '',

    // Company Information fields
    legalBusinessName: '',
    operatingAs: '',
    businessId: ''
  },
  subscriptionData: {
    employeesNumber: null,
    hiringClient: null
  },
  billingData: {
    firstName: '',
    lastName: '',
    country: '',
    countryIso3: '',
    address1: '',
    address2: '',
    city: '',
    region: '',
    postalCode: '',
    phone: '',
    email: ''
  },
  specialPricingData: {
    sku: null,
    specialPricingId: null
  },
  isSubcontractor: false
} as InitState;

export const createAccount = createAsyncThunk(
  'companyData/createAccount',
  async (accountData: AccountDataType, { dispatch }) => {
    try {
      const { data } = await Axios.post('/api/v1/billing/Company/', accountData);
      if (data && data.errors) {
        dispatch(errorCatch({ place: 'companyData/createAccount.rejected', data: data }));
        return Promise.reject(data);
      }
      return data;
    } catch (error) {
      dispatch(errorCatch({ place: 'companyData/createAccount.rejected', data: error }));
      return Promise.reject(error);
    }
  }
);

const slice = createSlice({
  name: 'companyData',
  initialState,
  reducers: {
    saveData: (state, action: PayloadAction<SignUpForm>) => {
      state.signUpData = action.payload;
    },
    saveBillingData: (state, action) => {
      state.billingData = action.payload;
    },
    saveSubscriptionData: (state, action: PayloadAction<SubscriptionDataType>) => {
      state.subscriptionData = action.payload;
    },
    saveSpecialPriceData: (state, action: PayloadAction<SpecialPricingData>) => {
      state.specialPricingData = action.payload;
    },
    saveIsSubcontractor: (state, action: PayloadAction<boolean>) => {
      state.isSubcontractor = action.payload;
    },
    clearBillingData: state => {
      state.billingData = initialState.billingData;
    }
  }
});

export const {
  saveData,
  saveBillingData,
  saveSubscriptionData,
  clearBillingData,
  saveSpecialPriceData,
  saveIsSubcontractor
} = slice.actions;
export default slice.reducer;
